import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@/lib/useApi";
import { Button } from "@/components/ui/button";
import { formatDate, label } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { FileDown, CheckCircle2, XCircle, FileText } from "lucide-react";
import { toast } from "sonner";

const kindLabel = {
  identita: "Documento d'identità",
  codice_fiscale: "Codice fiscale",
  visura: "Visura camerale",
  agreement_signed: "Accordo GoWeb firmato",
  integrative_signed: "Modulo integrativo firmato",
  order_signed: "Modulo d'ordine cliente firmato",
};

export default function AdminApprovals() {
  const api = useApi();
  const qc = useQueryClient();
  const { data = [], isLoading } = useQuery({
    queryKey: ["admin-approvals"],
    queryFn: async () => (await api.get("/admin/approvals")).data,
  });
  const [reasons, setReasons] = useState({});
  const onError = (e) => { qc.invalidateQueries({ queryKey: ["admin-approvals"] }); if (e.response?.status !== 409) toast.error(e.response?.data?.detail || "Errore"); };
  const approve = useMutation({
    mutationFn: async (id) => (await api.post(`/admin/approvals/${id}/approve`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-approvals"] }); toast.success("Approvato"); },
    onError,
  });
  const reject = useMutation({
    mutationFn: async (id) => (await api.post(`/admin/approvals/${id}/reject`, { reason: reasons[id] || "Non conforme" })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-approvals"] }); toast.success("Rifiutato"); },
    onError,
  });
  const busy = approve.isPending || reject.isPending;

  const openDoc = async (id) => {
    const r = await api.get(`/files/${id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    window.open(url, "_blank");
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Approvazioni</h1>
        <p className="text-sm text-slate-500 mt-1">Documenti e moduli in attesa di controllo e approvazione.</p>
      </div>
      {isLoading && <div className="text-slate-500">Caricamento…</div>}
      {!isLoading && data.length === 0 && (
        <div className="bg-white border border-[#E2DDD5] rounded-xl p-10 text-center text-slate-500">
          Nessuna pratica in attesa.
        </div>
      )}
      <div className="space-y-3">
        {data.map((d) => {
          const ctxReseller = d.context?.reseller;
          const ctxCustomer = d.context?.customer;
          return (
            <div key={d.id} className="bg-white border border-[#E2DDD5] rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`approval-${d.id}`}>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#F2F0EB] text-[#B84A2B] flex items-center justify-center">
                  <FileText size={18} />
                </div>
                <div>
                  <div className="font-semibold">{kindLabel[d.kind] || d.kind}</div>
                  <div className="text-sm text-slate-500">
                    {ctxReseller && <>Rivenditore <span className="font-mono">{ctxReseller.codice_reseller}</span> · {ctxReseller.company?.ragione_sociale}</>}
                    {ctxCustomer && <>Cliente {ctxCustomer.dati_fiscali?.ragione_sociale || `${ctxCustomer.anagrafica.nome} ${ctxCustomer.anagrafica.cognome}`}</>}
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5">Caricato il {formatDate(d.uploaded_at)}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Input value={reasons[d.id] || ""} onChange={(e) => setReasons({ ...reasons, [d.id]: e.target.value })} placeholder="Motivo rifiuto (opzionale)" className="h-9 w-56" data-testid={`reject-reason-${d.id}`} />
                <Button variant="outline" size="sm" onClick={() => openDoc(d.id)} data-testid={`btn-open-${d.id}`}><FileDown size={14} className="mr-1" /> Apri</Button>
                <Button variant="outline" size="sm" disabled={busy} onClick={() => reject.mutate(d.id)} className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid={`btn-reject-${d.id}`}><XCircle size={14} className="mr-1" /> Rifiuta</Button>
                <Button size="sm" disabled={busy} onClick={() => approve.mutate(d.id)} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid={`btn-approve-${d.id}`}><CheckCircle2 size={14} className="mr-1" /> Approva</Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
